// FLAGS: authorization, health-data
// Platform-wide session list with filters: role checks here decide which per-row mutations (reassign,
// cancel, reschedule, change status) SessionsView renders.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import authenticationService from "@src/services/authentication.service";
import { useAllSessionsQuery } from "@src/queries/experts";
import {
  SessionFilters,
  SessionsFilter,
} from "@src/components/SessionFilters/SessionFilters";
import {
  getFiltersFromStorage,
  removeFiltersFromStorage,
  saveFiltersToStorage,
} from "@src/utils/helpers";
import SessionsView from "./SessionsView";

let columns = [
  {
    id: "AllSessions.Date",
    width: 100,
  },
  {
    id: "AllSessions.ClientName",
    minWidth: 200,
  },
  {
    id: "AllSessions.Session",
    minWidth: 116,
  },
  {
    id: "AllSessions.Psychologist",
    minWidth: 100,
  },
  {
    id: "AllSessions.Status",
    width: 110,
  },
  {
    id: "Common.Actions",
    minWidth: 80,
  },
];

const AllSessionsView = () => {
  const { t } = useTranslation();
  const storeFiltersKey = "all-sessions";
  const defaultFilters = {
    searchTerm: "",
    status: undefined,
    startedAtFrom: undefined,
    startedAtTo: undefined,
  };
  const { filters: storedFilters, page: storedPage } = getFiltersFromStorage(
    storeFiltersKey,
  ) ?? {
    filters: defaultFilters,
    page: 0,
  };
  const [page, setPage] = useState(storedPage);
  const [filters, setFilters] = useState<SessionsFilter>(storedFilters);

  const {
    data: {
      data: consults = [],
      pagination: { total = 0, hasNextPage = false } = {},
    } = {},
    isFetching,
  } = useAllSessionsQuery({
    page,
    status: filters.status,
    search: filters.searchTerm,
    startedAtFrom: filters.startedAtFrom,
    startedAtTo: filters.startedAtTo,
    employerId: filters.employer?.id,
    expertId: filters.expert?.id ?? undefined,
  });

  const {
    isManagerOrAdmin,
    isManagerOrAdminOrHost,
    isManagerOrAdminOrSupport,
    isManagerOrAdminOrHostOrSupport,
  } = authenticationService;
  if (!isManagerOrAdminOrHost()) {
    // remove reassign column
    columns = columns.filter((col) => col.id !== "");
  }

  const showReferralToolkit = false;
  return (
    <>
      <SessionFilters
        filters={filters}
        searchPlaceholder={t("AllSessions.List.SearchPlaceholder")}
        onFilterChanged={(f) => {
          saveFiltersToStorage(storeFiltersKey, f, 0);
          setPage(0);
          setFilters(f);
        }}
        onFilterCleared={() => {
          removeFiltersFromStorage(storeFiltersKey);
          setPage(0);
          setFilters(defaultFilters);
        }}
      />
      <SessionsView
        titleText={t("AllSessions.PageTitle")}
        placeholderSearch={t("AllSessions.List.SearchPlaceholder")}
        page={page}
        onPageChange={(p: number) => {
          saveFiltersToStorage(storeFiltersKey, filters, p);
          setPage(p);
        }}
        columns={columns}
        isFetching={isFetching}
        consults={consults}
        total={total}
        hasNextPage={hasNextPage}
        showPsychologistName
        showReassign={isManagerOrAdminOrHost()}
        showJoinSession={false}
        showRescheduleSession={isManagerOrAdminOrHost()}
        showCancelSession={isManagerOrAdminOrHostOrSupport()}
        showSessionDetails={isManagerOrAdmin()}
        showChangeSessionStatus={isManagerOrAdminOrSupport()}
        showCopyJoinLink={isManagerOrAdminOrHost()}
        showReferralToolkitIcon={showReferralToolkit}
      />
    </>
  );
};

export default AllSessionsView;
